import { useApolloClient } from '@apollo/react-hooks';
import XLSX from 'xlsx';

import { getCourseCodeReport, getTKCourseCodeReport } from 'GraphQL/queries/report.query';
import { CONTENT_TYPE, MODULE_STATUS_TH } from 'Domains/module';
import { formatSchoolTerm } from 'Domains/utils';
import { SYLLABUS_MODE } from 'Domains/syllabus';

const ExportCourseCodeReportData = props => {
  const {
    schoolTermId,
    schoolGradeId,
    classNo,
    subjectId,
    courseCode,
    schoolCode,
    mode,
    quizFilter,
    children,
  } = props;
  const client = useApolloClient();

  const fetchCourseReportData = async () => {
    try {
      const { data } = await client.query({
        query: mode === SYLLABUS_MODE.TRADITIONAL ? getCourseCodeReport : getTKCourseCodeReport,
        variables: {
          schoolTermId,
          schoolGradeId,
          classNo,
          subjectId,
          courseCode,
          schoolCode,
        },
      });
      return { data };
    } catch (error) {
      return { error };
    }
  };

  const getQuizArchetypesScoreAndTotal = (index, quizArchetypes) => {
    let score = quizArchetypes?.[index]?.firstScore;
    const total = quizArchetypes?.[index]?.total;

    if (quizFilter === 'max') {
      score = quizArchetypes?.[index]?.bestScore;
    } else if (quizFilter === 'avg') {
      score = quizArchetypes?.[index]?.avg;
    }

    return { score, total };
  };

  const exportCourseCodeReport = async ({ reports, type }) => {
    const {
      schoolTerm: { shortName: schoolTerm } = {},
      schoolGrade: { shortName: schoolGrade } = {},
      classNo,
      subject: { name: subject } = {},
      courseName,
    } = reports[0];

    const reportData =
      mode === SYLLABUS_MODE.TRADITIONAL
        ? [
            ...reports.map(report => {
              const {
                schoolGrade: { shortName: schoolGrade },
                classNo,
                schoolTerm: { shortName: schoolTerm },
                subject: { name: subject },
                courseName,
                sectionName,
                subsectionName,
                contentType,
                totalAttemptedStudents,
                status,
              } = report;
              return [
                schoolTerm,
                schoolGrade,
                `${schoolGrade}/${classNo}`,
                subject,
                `${courseName} (${courseCode})`,
                sectionName,
                subsectionName,
                contentType === 'Quiz' ? CONTENT_TYPE.QUIZ : CONTENT_TYPE.VDO,
                totalAttemptedStudents,
                MODULE_STATUS_TH[status],
                ...(contentType === 'Quiz'
                  ? [
                      report.quizResult.good,
                      report.quizResult.normal,
                      report.quizResult.needImprovement,
                    ]
                  : ['', '', '']),
              ];
            }),
          ]
        : [
            ...reports.map(report => {
              const {
                sectionName,
                sectionGroupName,
                totalStudentCompletedVideo,
                totalStudentCompletedQuiz,
                totalStudents,
                isContainVideoModule,
                isContainQuizModule,
                quizArchetypes,
              } = report;

              const quizColumns = !isContainQuizModule
                ? ['', '', '', '']
                : Array.from({ length: 4 }, (_, i) => {
                    const { score, total } = getQuizArchetypesScoreAndTotal(i, quizArchetypes);
                    return total > 0 ? `${score}/${total}` : '';
                  });
              const isAllStudentCompletedVideo = totalStudentCompletedVideo === totalStudents;
              const isAllStudentCompletedQuiz = totalStudentCompletedQuiz === totalStudents;

              return [
                sectionName,
                sectionGroupName,
                isContainVideoModule ? `${totalStudentCompletedVideo}/${totalStudents}` : '',
                isContainVideoModule
                  ? isAllStudentCompletedVideo
                    ? 'เข้าเรียนครบ'
                    : 'เข้าเรียนไม่ครบ'
                  : '',
                isContainQuizModule ? `${totalStudentCompletedQuiz}/${totalStudents}` : '',
                isContainQuizModule
                  ? isAllStudentCompletedQuiz
                    ? 'เข้าสอบครบ'
                    : 'เข้าสอบไม่ครบ'
                  : '',
                ...quizColumns,
              ];
            }),
          ];

    const formattedReportAOA = [
      ['ภาพรวมการเข้าเรียนและการทำข้อสอบของนักเรียนรายห้อง'],
      mode === SYLLABUS_MODE.TRADITIONAL
        ? [
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            '',
            'การเข้าเรียนของนักเรียน',
            'การเข้าเรียนของนักเรียน',
            'ผลการทำข้อสอบท้ายหน่วยของนักเรียน',
            'ผลการทำข้อสอบท้ายหน่วยของนักเรียน',
            'ผลการทำข้อสอบท้ายหน่วยของนักเรียน',
          ]
        : [
            '',
            '',
            'ภารกิจสำเร็จ (จำนวนคน)',
            'ภารกิจสำเร็จ (จำนวนคน)',
            'ภารกิจสำเร็จ (จำนวนคน)',
            'ภารกิจสำเร็จ (จำนวนคน)',
            'ประเภทชุดคำถาม (คะแนน)',
            'ประเภทชุดคำถาม (คะแนน)',
            'ประเภทชุดคำถาม (คะแนน)',
            'ประเภทชุดคำถาม (คะแนน)',
          ],
      mode === SYLLABUS_MODE.TRADITIONAL
        ? [
            'เทอม',
            'ระดับชั้น',
            'ห้อง',
            'วิชา',
            'หน่วย',
            'บท',
            'เรื่อง',
            'ประเภทไฟล์',
            'จำนวนนักเรียน',
            'สถานะ',
            'ดี (80% ขึ้นไป)',
            'พอใช้ (60-79%)',
            'ควรปรับปรุง (ต่ำกว่า 60%)',
          ]
        : [
            'บท',
            'ภารกิจ',
            'Video',
            'สถานะการเรียน',
            'Quiz',
            'สถานะการสอบ',
            'เสริมความมั่นใจ',
            'เช็คความเข้าใจ',
            'ลดความเข้าใจผิด',
            'พร้อมสอบหรือยัง',
          ],
      ...reportData,
    ];
    const worksheet = XLSX.utils.aoa_to_sheet(formattedReportAOA);
    worksheet['!merges'] =
      mode === SYLLABUS_MODE.TRADITIONAL
        ? [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 12 } }, //ภาพรวมการเข้าเรียนและการทำข้อสอบของนักเรียนรายห้อง
            { s: { r: 1, c: 8 }, e: { r: 1, c: 9 } }, //การเข้าเรียนของนักเรียน
            { s: { r: 1, c: 10 }, e: { r: 1, c: 12 } }, //ผลการทำข้อสอบท้ายหน่วยของนักเรียน
          ]
        : [
            { s: { r: 0, c: 0 }, e: { r: 0, c: 9 } }, //ภาพรวมการเข้าเรียนและการทำข้อสอบของนักเรียนรายห้อง
            { s: { r: 1, c: 2 }, e: { r: 1, c: 5 } }, //ภารกิจสำเร็จ
            { s: { r: 1, c: 6 }, e: { r: 1, c: 9 } }, //Quiz Archetypes
          ];
    worksheet['!cols'] =
      mode === SYLLABUS_MODE.TRADITIONAL
        ? [
            { wch: 10 }, //ระดับชั้น
            { wch: 10 }, //ห้อง
            { wch: 10 }, //เทอม
            { wch: 25 }, //วิชา
            { wch: 25 }, //หน่วย
            { wch: 30 }, //บท
            { wch: 30 }, //เรื่อง
            { wch: 10 }, //ประเภทไฟล์ / Video
            { wch: 15 }, //จำนวนนักเรียน / Quiz
            { wch: 15 }, //สถานะ / เสริมความมั่นใจ
            { wch: 15 }, //ดี / เช็คความจำ
            { wch: 15 }, //พอใช้ / ลดความเข้าใจผิด
            { wch: 15 }, //ปรับปรุง / พร้อมสอบ
          ]
        : [
            { wch: 30 }, //บท
            { wch: 30 }, //เรื่อง
            { wch: 10 }, //Video
            { wch: 15 }, //สถานะการเรียน
            { wch: 10 }, //Quiz
            { wch: 15 }, //สถานะการสอบ
            { wch: 15 }, //เสริมความมั่นใจ
            { wch: 15 }, //เช็คความจำ
            { wch: 15 }, //ลดความเข้าใจผิด
            { wch: 15 }, //พร้อมสอบ
          ];
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'รายห้อง-เข้าเรียนและทำข้อสอบ');
    XLSX.writeFile(
      workbook,
      `ภาพรวมรายห้อง_${formatSchoolTerm(
        schoolTerm,
      )}_${schoolGrade}-${classNo}_${subject}_${courseName}.` + (type || 'xlsx'),
    );
  };

  return children({ fetchCourseReportData, exportCourseCodeReport });
};

export default ExportCourseCodeReportData;
